// Composes the home screen and shared workspace from focused UI components.
import type { Color, RoomState } from './types';
import { boardFromFen } from './board/pieces';
import { HomeScreen } from './components/HomeScreen';
import { GameOverModal } from './components/GameOverModal';
import { PromotionModal } from './components/PromotionModal';
import { TrainerGamePanel, GameClockRow } from './components/game/GamePanels';
import { LecturePositionPanel, LectureTaskTimerPanel, LectureStudentTimer } from './components/lecture/LecturePanels';
import { RoomActivitySidebar } from './components/room/RoomActivitySidebar';
import { RoomModePanel } from './components/room/RoomModePanel';
import { RoomSetupPanel } from './components/room/RoomSetupPanel';
import { BoardWorkspace } from './components/workspace/BoardWorkspace';
import { useAppController } from './hooks/useAppController';
import './styles.css';
import './fonts.css';

function clockText(ms: number) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  return Math.floor(total / 60).toString().padStart(2, '0') + ':' + (total % 60).toString().padStart(2, '0');
}

export default function App() {
  const {
    screen, role, name, password, roomInput, roomId, state, board, animationVersion, pendingGameMove,
    orientation, selected, spare, recording, recorded, lectureSeconds,
    lectureSecondsInputRef, gameMinutes, gameIncrement, trainerColor, startingTurn,
    selectedMode, studentList, exerciseFrozen, attempts, studentEvents,
    studentMoveHistory, reviewHistory, gameOver, pendingPromotion, status, connected,
    lectureRemaining, uiMode, lecture, displayedTurn, whiteClock, blackClock,
    setName, setPassword, setRoomInput, setOrientation, setSelected, setSpare,
    setLectureSeconds, setGameMinutes, setGameIncrement, setTrainerColor,
    setStartingTurn, setGameOver, setPendingPromotion, setReviewIndex, setState,
    setBoard, setStatus, setRoleAndWorkspace, leaveToHome, createRoom, joinRoom,
    selectMode, startRecording, finishRecording, setLectureTurn, clearBoard,
    removePiece, resetPosition, startLectureTimer, adjustLectureTimer,
    stopLectureTimer, finishGame, resetPawnsGame, startGame, boardClick,
    boardDrop, retryExercise, completePromotion, drawArrow, toggleHighlight,
    clearGraphics, exportReview, undoLastMove
  } = useAppController();

  if (screen === 'home') {
    return <HomeScreen onSelectRole={setRoleAndWorkspace} />;
  }

  const review = state?.mode === 'lecture' && reviewHistory.length > 0;
  const boardInteractive = (() => {
    if (!roomId || !state || pendingGameMove) return false;

    if (role === 'trainer') {
      if (state.mode === 'lecture') {
        return uiMode === 'lecture';
      }
      return uiMode !== 'lecture';
    }

    if (state.mode === 'lecture') {
      return Boolean(state.exercise) && state.lectureTimer.active && !exerciseFrozen;
    }
    return state.gameTimer.active;
  })();

  const setupSidebar = !roomId ? (
    <RoomSetupPanel
      role={role}
      name={name}
      password={password}
      roomInput={roomInput}
      onNameChange={setName}
      onPasswordChange={setPassword}
      onRoomInputChange={setRoomInput}
      onCreateRoom={createRoom}
      onJoinRoom={joinRoom}
    />
  ) : (
    <>
      {role === 'trainer' && roomId && lecture && (
        <LecturePositionPanel
          turn={state?.turn || 'w'}
          onResetPosition={resetPosition}
          onClearBoard={clearBoard}
          onSetTurn={setLectureTurn}
        />
      )}
      {role === 'trainer' && roomId && !lecture && (
        <TrainerGamePanel
          state={state}
          gameMinutes={gameMinutes}
          gameIncrement={gameIncrement}
          trainerColor={trainerColor}
          startingTurn={startingTurn}
          onGameMinutesChange={setGameMinutes}
          onGameIncrementChange={setGameIncrement}
          onTrainerColorChange={setTrainerColor}
          onStartingTurnChange={setStartingTurn}
          onStartGame={startGame}
          onUndo={undoLastMove}
          onFinishStandardGame={finishGame}
          onFinishPawnsGame={resetPawnsGame}
        />
      )}
      {role === 'trainer' && roomId && state && state.mode !== 'lecture' && state.history.length > 0 && (
        <section className="panel">
          <button className="secondary full" onClick={exportReview}>Разобрать в лекции</button>
        </section>
      )}
    </>
  );

  function selectReviewEntry(entry: RoomState['history'][number], index: number) {
    const nextTurn: Color = entry.fen.split(/\s+/)[1] === 'b' ? 'b' : 'w';
    setReviewIndex(index);
    setBoard(boardFromFen(entry.fen));
    setState((prev) => prev ? { ...prev, fen: entry.fen, turn: nextTurn } : prev);
    setStatus('Ход ' + entry.san);
  }

  const activitySidebar = (
    <RoomActivitySidebar
      role={role}
      roomId={roomId}
      state={state}
      review={review}
      reviewHistory={reviewHistory}
      studentList={studentList}
      studentMoveHistory={studentMoveHistory}
      studentEvents={studentEvents}
      attempts={attempts}
      onSelectReview={selectReviewEntry}
    />
  );

  return (
    <main className="app">
      <header className="topbar">
        <div className="top-actions">
          <span
            className={'connected-dot' + (connected ? '' : ' connected-dot-offline')}
            title={connected ? 'Соединение с сервером установлено' : 'Соединение с сервером потеряно'}
            aria-label={connected ? 'Соединение установлено' : 'Соединение потеряно'}
          >●</span>
          <button className="ghost" onClick={leaveToHome}>На главную</button>
        </div>
      </header>

      <div className={'layout' + (roomId ? ' has-room' : '')}>
        <aside className="side-column">
          {roomId ? activitySidebar : setupSidebar}
        </aside>

        <BoardWorkspace
          role={role}
          roomId={roomId}
          state={state}
          board={board}
          orientation={orientation}
          animationVersion={animationVersion}
          selected={selected}
          interactive={boardInteractive}
          recording={recording}
          spare={spare}
          onRemovePiece={removePiece}
          onSquareClick={boardClick}
          onDropPiece={boardDrop}
          onSparePiece={(piece) => {
            setSpare(piece);
            setSelected(null);
          }}
          onRotate={() => setOrientation((current) => current === 'white' ? 'black' : 'white')}
          onArrow={drawArrow}
          onHighlight={toggleHighlight}
          onClearGraphics={clearGraphics}
        >
          {role === 'trainer' && roomId && lecture && (
            <LectureTaskTimerPanel
              recording={recording}
              recorded={recorded}
              sequence={state?.exercise?.sequence}
              seconds={lectureSeconds}
              remainingMs={lectureRemaining}
              timerActive={Boolean(state?.lectureTimer.active)}
              inputRef={lectureSecondsInputRef}
              onStartRecording={startRecording}
              onFinishRecording={finishRecording}
              onRetry={retryExercise}
              onAdjustTimer={adjustLectureTimer}
              onSecondsChange={setLectureSeconds}
              onStartTimer={startLectureTimer}
              onStopTimer={stopLectureTimer}
              formatClock={clockText}
            />
          )}

          {roomId && !lecture && (
            <GameClockRow whiteClock={clockText(whiteClock)} blackClock={clockText(blackClock)} />
          )}
        </BoardWorkspace>

        {roomId && (
          <aside className="side-column">
            <RoomModePanel role={role} displayedTurn={displayedTurn} selectedMode={selectedMode} state={state} onSelectMode={selectMode} />
            {role === 'student' && state?.mode === 'lecture' && (
              <LectureStudentTimer active={state.lectureTimer.active} remainingMs={lectureRemaining} formatClock={clockText} />
            )}
            {setupSidebar}
          </aside>
        )}
      </div>

      {gameOver && (
        <GameOverModal
          result={gameOver}
          onClose={() => setGameOver(null)}
        />
      )}

      {pendingPromotion && (
        <PromotionModal
          from={pendingPromotion.from}
          to={pendingPromotion.to}
          color={pendingPromotion.color}
          onSelect={completePromotion}
          onCancel={() => setPendingPromotion(null)}
        />
      )}
    </main>
  );
}
